import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import GlossaryLayout from "@/components/GlossaryLayout";
import useSEO from "@/hooks/useSEO";

const PILLARS = [
  {
    number: "01",
    name: "Intake and classification",
    description:
      "Every piece of knowledge that enters consideration for AI use is identified, described, and classified by contributor class before any review begins. Intake is the governance boundary — the point at which raw operational data becomes a governed knowledge artifact.",
  },
  {
    number: "02",
    name: "Policy review",
    description:
      "Each candidate artifact is examined for policy violations before publication. Policy review checks for named-person phrasing, PII exposure, scope ambiguity, attribution issues, and compliance conflicts. The outcome — approval, rejection, or conditional approval with modifications — is logged in the evidence record.",
  },
  {
    number: "03",
    name: "Evidence records and audit trail",
    description:
      "Every governance decision is documented in an immutable evidence record: who reviewed it, when, what they decided, and why. The evidence record is the legal and operational foundation for demonstrating that your AI knowledge has been governed — not just asserted to have been.",
  },
  {
    number: "04",
    name: "Publication gating",
    description:
      "Artifacts cannot enter AI systems until they have cleared the appropriate review gate for their contributor class. Publication gating is the enforcement mechanism that makes the rest of the framework meaningful.",
  },
  {
    number: "05",
    name: "Revocation and downstream impact analysis",
    description:
      "When a published artifact must be withdrawn — because it was wrong, outdated, discriminatory, or legally problematic — revocation freezes the artifact and triggers an analysis of which AI behaviours, cases, and decisions depended on it. Revocation without impact analysis is incomplete governance.",
  },
];

const COMPARISONS = [
  {
    domain: "AI safety",
    focus: "Model-level — preventing harmful outputs, alignment, adversarial robustness.",
    difference:
      "AI safety is about what the model does. AI knowledge governance is about what the model is allowed to learn from and act on. A perfectly safe model can still encode wrong, biased, or non-compliant operational knowledge.",
  },
  {
    domain: "AI ethics",
    focus: "Principle-level — fairness, transparency, accountability, non-discrimination.",
    difference:
      "Ethics provides the principles; governance provides the operational mechanism. Saying \"our AI should be fair\" is an ethical commitment. Having a classification and review workflow that prevents biased operational patterns from entering AI context is knowledge governance.",
  },
  {
    domain: "Data governance",
    focus: "Storage, access, retention, and quality of raw data assets.",
    difference:
      "Data governance asks: who can access this data and how must it be stored? Knowledge governance asks: can this pattern, extracted from that data, be encoded into AI behaviour — and on what basis? A compliant data store can still produce non-compliant knowledge artifacts.",
  },
  {
    domain: "Model governance",
    focus: "Versioning, deployment approval, performance monitoring of ML models.",
    difference:
      "Model governance tracks what version of a model is deployed and how it performs. Knowledge governance tracks what the model is allowed to know. The two are complementary — model governance without knowledge governance leaves the training signal and retrieval context ungoverned.",
  },
];

const FAQS = [
  {
    q: "Who owns AI knowledge governance in an organisation?",
    a: "Ownership is typically distributed. The Head of AI or Chief AI Officer sets the framework and tooling. Legal and Compliance own the policy standards that determine what can and cannot be encoded. Business unit leaders own the classification and review of artifacts from their domains. IT or Data Engineering owns the technical pipelines that implement the publication gates. Without a clear RACI, knowledge governance tends to collapse into de facto ungoverned extraction.",
  },
  {
    q: "Do we need AI knowledge governance if we are only using RAG, not fine-tuning?",
    a: "Yes. Retrieval-augmented generation (RAG) feeds knowledge artifacts directly into model context at inference time — meaning ungoverned artifacts affect every query, not just training runs. In some respects, RAG makes knowledge governance more urgent: the knowledge update cycle is faster, the volume of artifacts is higher, and the impact of a problematic artifact is immediate rather than baked into model weights.",
  },
  {
    q: "What regulations require AI knowledge governance?",
    a: "No single regulation yet mandates AI knowledge governance by name, but multiple regulatory frameworks create obligations that knowledge governance addresses: GDPR (especially around automated decision-making and profiling), the EU AI Act (transparency and data governance requirements for high-risk AI), PCI-DSS (protecting payment data from entering AI training pipelines), and sector-specific guidance from the FCA, SEC, and HHS in financial services, and healthcare. Governance frameworks that can demonstrate documented review and audit trails are significantly better positioned as regulatory scrutiny increases.",
  },
  {
    q: "How is AI knowledge governance different from prompt engineering?",
    a: "Prompt engineering is a technique for guiding model behaviour at inference time. AI knowledge governance is the framework that determines what the model is allowed to know — whether through its training data, its retrieval context, or its system prompt. A well-engineered prompt built on ungoverned knowledge is still a governance gap. Governance happens before the prompt is written.",
  },
  {
    q: "What does 'ungoverned' knowledge look like in practice?",
    a: "Ungoverned knowledge is the operational default at most organisations today. Support teams extract patterns from Zendesk and paste them into ChatGPT system prompts. Engineers copy KB articles into vector stores without checking for PII or outdated policy. Sales teams fine-tune models on call transcripts without classifying whether those transcripts contain protected customer information. None of this is malicious — it is simply the gap between AI capability and AI governance maturity.",
  },
];

export default function AiKnowledgeGovernance() {
  useSEO({
    title: "What is AI Knowledge Governance? — ProofMark Glossary",
    description:
      "AI knowledge governance is the practice of controlling what operational knowledge can enter AI systems, through what review process, and how it can be revoked. Learn how it differs from AI safety, data governance, and model governance — and why it matters.",
    path: "/glossary/ai-knowledge-governance",
  });

  useEffect(() => {
    const scriptId = "proofmark-jsonld-defined-term";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: "AI Knowledge Governance",
      description:
        "The practice of managing what operational knowledge can enter AI systems — on what basis, through what review workflow, and how it can be revoked — maintaining an immutable audit trail of every governance decision.",
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        name: "ProofMark AI Governance Glossary",
        url: "https://proofmark-frontend-production.up.railway.app/glossary/ai-knowledge-governance",
      },
    });
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <GlossaryLayout>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground transition-colors">ProofMark</Link>
        <span>/</span>
        <span>Glossary</span>
        <span>/</span>
        <span className="text-foreground">AI Knowledge Governance</span>
      </nav>

      {/* Hero */}
      <div className="mb-10">
        <span className="tracked-caps">AI Governance Glossary</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-4">AI Knowledge Governance</h1>
        <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed">
          The practice of managing what operational knowledge can enter AI systems — on what basis,
          through what review workflow, and how it can be revoked — with an immutable audit trail
          for every governance decision.
        </p>
      </div>

      {/* Definition box */}
      <div className="border-l-4 border-brand pl-6 py-4 bg-brand/5 rounded-r-lg mb-12">
        <p className="text-xs tracked-caps text-muted-foreground mb-2">Definition</p>
        <p className="text-base leading-relaxed">
          <strong>AI knowledge governance</strong> is a framework of policies, processes, and tools
          that controls the lifecycle of operational knowledge as it moves from human work into AI
          systems. It determines which knowledge artifacts can be extracted, how they must be
          classified and reviewed, under what conditions they can be published into AI context, and
          how they can be revoked and their downstream impact assessed when problems are found.
        </p>
      </div>

      {/* Why this exists */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">
          Why AI knowledge governance is a distinct problem
        </h2>
        <div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed">
          <p>
            Enterprise AI systems do not generate knowledge — they amplify it. When an AI customer
            support agent resolves a chargeback dispute, it is applying patterns extracted from
            thousands of prior resolutions. When an AI sales assistant handles an objection, it is
            reproducing reasoning encoded from past conversations. The AI&apos;s behaviour is only as
            sound as the knowledge it was given.
          </p>
          <p>
            Most organisations govern the model and the data store, but not the knowledge in
            between. They have data governance frameworks that manage who can access support tickets.
            They have model governance that tracks which version of a model is deployed. But the
            step where patterns are extracted from those tickets and encoded into model behaviour —
            the step that most directly shapes what the AI actually does — is typically ungoverned.
          </p>
          <p>
            AI knowledge governance fills this gap. It sits between human operational work and AI
            behaviour, ensuring that every piece of knowledge that crosses that boundary has been
            classified, reviewed, approved, and documented — and that it can be revoked if it turns
            out to be wrong.
          </p>
        </div>
      </section>

      {/* The cost of no governance */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">
          What ungoverned AI knowledge looks like
        </h2>
        <div className="max-w-3xl">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                title: "PII encoded into AI behaviour",
                desc: "Customer card numbers, account details, or contact information embedded in KB articles or escalation rules get ingested into AI context — often without anyone realising the source contained PII.",
              },
              {
                title: "Outdated rules that no one can revoke",
                desc: "A pricing policy from 18 months ago is still in the AI's context because there is no mechanism to track which knowledge artifacts are live or trigger a review when the underlying policy changes.",
              },
              {
                title: "Voice mimicry without consent",
                desc: "Patterns inferred from a single agent's ticket history get encoded into an AI system that then replicates that agent's voice, reasoning style, and implicit biases at scale.",
              },
              {
                title: "No audit trail for regulators",
                desc: "When a regulator asks how a particular AI decision was reached, there is no way to trace it to a specific knowledge artifact, because no evidence record was maintained.",
              },
            ].map((item) => (
              <div key={item.title} className="border border-rose-200 rounded-lg p-4 bg-rose-50">
                <p className="font-medium text-sm text-rose-700 mb-1.5">{item.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Five pillars */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-6">
          The five pillars of AI knowledge governance
        </h2>
        <div className="space-y-5 max-w-3xl">
          {PILLARS.map((p) => (
            <div key={p.number} className="border border-border rounded-lg p-5 bg-card flex gap-5">
              <span className="font-mono text-2xl text-muted-foreground/30 shrink-0 leading-none mt-0.5">
                {p.number}
              </span>
              <div>
                <p className="font-medium text-sm mb-2">{p.name}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How it differs */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-6">
          AI knowledge governance vs adjacent disciplines
        </h2>
        <div className="space-y-4 max-w-3xl">
          {COMPARISONS.map((c) => (
            <div key={c.domain} className="border border-border rounded-lg p-5 bg-card">
              <p className="font-medium text-sm mb-1">{c.domain}</p>
              <p className="text-xs text-muted-foreground mb-3 italic">{c.focus}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.difference}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Maturity tool CTA */}
      <div className="border border-brand/30 rounded-lg p-5 bg-brand/5 max-w-2xl mb-12">
        <p className="font-medium text-sm mb-2">Where does your organisation stand?</p>
        <p className="text-sm text-muted-foreground mb-3">
          The AI Governance Maturity tool scores your current practices across classification,
          review, audit, and revocation — and tells you what to address first.
        </p>
        <Link
          to="/tools/ai-governance-maturity"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Take the free assessment →
        </Link>
      </div>

      {/* Related terms */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-4">Related terms</h2>
        <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
          <Link
            to="/glossary/ai-knowledge-artifact"
            className="border border-border rounded-lg p-4 bg-card hover:border-brand/40 transition-colors group"
          >
            <p className="font-medium text-sm group-hover:text-brand transition-colors">AI Knowledge Artifact →</p>
            <p className="text-xs text-muted-foreground mt-1">
              The discrete unit that AI knowledge governance operates on.
            </p>
          </Link>
          <Link
            to="/glossary/contributor-class"
            className="border border-border rounded-lg p-4 bg-card hover:border-brand/40 transition-colors group"
          >
            <p className="font-medium text-sm group-hover:text-brand transition-colors">Contributor Class →</p>
            <p className="text-xs text-muted-foreground mt-1">
              The taxonomy used to classify knowledge sources at intake.
            </p>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="font-serif-display text-2xl mb-6">Frequently asked questions</h2>
        <div className="space-y-5 max-w-3xl">
          {FAQS.map((faq) => (
            <div key={faq.q} className="border border-border rounded-lg p-5 bg-card">
              <p className="font-medium text-sm mb-2">{faq.q}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="border border-brand/30 rounded-lg p-6 bg-brand/5 max-w-2xl">
        <p className="font-medium mb-2">Build AI knowledge governance into your organisation</p>
        <p className="text-sm text-muted-foreground mb-4">
          ProofMark is built around the five pillars of AI knowledge governance — intake,
          classification, review, publication gating, and revocation — with an immutable evidence
          record at every step.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Request early access
          </Link>
          <Link
            to="/tools/policy-checker"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium hover:border-brand/40 transition-colors"
          >
            Try the free Policy Checker →
          </Link>
        </div>
      </div>
    </GlossaryLayout>
  );
}
